import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Wifi as WifiIcon,
  RefreshCw,
  Loader2,
  Bluetooth,
  Smartphone,
  Check,
} from "lucide-react";
import clsx from "clsx";
import { Api } from "../lib/api.js";

/**
 * Wi-Fi settings — the native in-app version of the Pi's standalone setup
 * page (which still lives at http://192.168.4.1/wifi for the hotspot
 * provisioning flow). Talks to the same JSON endpoints; on the cloud host
 * those proxy to the Pi through the reverse tunnel.
 *
 * Joins go through the server-side command queue (the Pi polls it every
 * ~15 s over whatever connection it has), never the direct /wifi/connect
 * call — so a join works even when the only backhaul is the BT lifeline.
 */

type Banner = { kind: "ok" | "err" | "info"; node: ReactNode } | null;

const INPUT_CLS =
  "w-full bg-ink/60 border border-hairline rounded-md px-3 py-2 text-ivory focus:border-gold outline-none";

function BannerBox({ banner }: { banner: Banner }): JSX.Element | null {
  if (!banner) return null;
  return (
    <div
      className={clsx(
        "rounded-md border px-3 py-2.5 text-sm leading-relaxed",
        banner.kind === "ok" && "bg-jade/15 border-jade/50 text-emerald-300",
        banner.kind === "err" && "bg-vermilion/15 border-vermilion/50 text-vermilionHi",
        banner.kind === "info" && "bg-gold/10 border-gold/40 text-goldHi",
      )}
    >
      {banner.node}
    </div>
  );
}

function Row({ label, value }: { label: string; value: ReactNode }): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-4 bg-ink/60 border border-hairline rounded-md px-3 py-2">
      <span className="section-label text-[10px]">{label}</span>
      <span className="font-mono text-sm text-goldHi text-right">{value}</span>
    </div>
  );
}

/** "just now" / "45 s ago" / "12 min ago" / "3 h ago" */
function ago(iso: string): string {
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 10) return "just now";
  if (s < 60) return `${s} s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.round(h / 24)} d ago`;
}

export function Wifi(): JSX.Element {
  const [banner, setBanner] = useState<Banner>(null);

  // ── Which Pi? Multi-Pi households (2026-10-28): the selector drives every
  // call on this page — each carries ?device= and each queryKey includes it,
  // so switching Pis refetches everything. An empty/failed device list means
  // the legacy single-Pi behaviour (no device param, PI_TUNNEL_PORT
  // fallback). ─────────────────────────────────────────────────────────────
  const devicesQ = useQuery({
    queryKey: ["cast-devices"],
    queryFn: Api.cast.devices,
    refetchInterval: 15_000,
    retry: false,
  });
  const pis = devicesQ.data?.devices ?? [];
  const [picked, setPicked] = useState<string | undefined>(undefined);
  const deviceId = picked ?? pis[0]?.device_id;
  const selectedLabel = pis.find((p) => p.device_id === deviceId)?.label ?? null;

  // ── Current connection ──────────────────────────────────────────────────
  const statusQ = useQuery({
    queryKey: ["wifi-status", deviceId],
    queryFn: () => Api.wifi.status(deviceId),
    refetchInterval: 10_000,
    retry: false,
  });
  const status = statusQ.data;
  const unreachable = statusQ.isError;

  // ── Last queued command — served from the cloud DB, so it works even when
  // the live tunnel to the box is down. This is the record of what the box
  // last did (and what it ACTUALLY joined, once the box reports detail). ────
  const latestCmdQ = useQuery({
    queryKey: ["wifi-latest-cmd", deviceId],
    queryFn: () => Api.wifi.latestCommand(deviceId),
    refetchInterval: 10_000,
    retry: false,
  });
  const lastCmd = latestCmdQ.data?.command ?? null;

  // ── Network scan ────────────────────────────────────────────────────────
  const [ssid, setSsid] = useState("");
  const [manualSsid, setManualSsid] = useState("");
  const [password, setPassword] = useState("");
  const scanQ = useQuery({
    queryKey: ["wifi-scan", deviceId],
    queryFn: () => Api.wifi.scan(deviceId),
    retry: false,
    // A scan takes several seconds on the radio — don't hammer it.
    staleTime: 60_000,
    enabled: !unreachable,
  });
  const networks = scanQ.data?.networks ?? [];
  // A typed name wins over the dropdown — needed for hidden networks and for
  // any time the scan can't run (AP mode, tunnel down).
  const effectiveSsid = manualSsid.trim() || ssid;

  // Pre-select the network from the most recent join attempt (Pi supplies
  // history; a tokenless cloud status doesn't, but the app is always authed).
  const preselected = useRef(false);
  useEffect(() => {
    if (preselected.current || !status?.history?.[0]?.ssid || networks.length === 0) return;
    if (networks.some((n) => n.ssid === status.history![0]!.ssid)) {
      setSsid(status.history![0]!.ssid);
      preselected.current = true;
    }
  }, [status, networks]);

  // ── Queued connect + result watch ───────────────────────────────────────
  const [connecting, setConnecting] = useState<{ id: string; ssid: string } | null>(null);

  async function doConnect(): Promise<void> {
    if (!effectiveSsid || connecting) return;
    setBanner(null);
    try {
      const q = await Api.wifi.queueConnect(deviceId, { ssid: effectiveSsid, password: password.trim() || null });
      if (!q.ok || !q.id) throw new Error(q.error ?? "queue failed");
      setConnecting({ id: q.id, ssid: effectiveSsid });
      setBanner({
        kind: "info",
        node: "Queued on the server — the box picks it up within ~15 s over whatever connection it has (even the phone lifeline). Watching for the result…",
      });
    } catch (e) {
      setBanner({ kind: "err", node: `✗ ${(e as Error).message}` });
    }
  }

  useEffect(() => {
    if (!connecting) return;
    let tries = 0;
    const t = window.setInterval(() => {
      tries += 1;
      void (async () => {
        try {
          const { command: c } = await Api.wifi.latestCommand(deviceId);
          if (c && c.id === connecting.id) {
            if (c.state === "running") {
              setBanner({ kind: "info", node: `Joining ${connecting.ssid}…` });
            } else if (c.state === "done") {
              window.clearInterval(t);
              setConnecting(null);
              // The box reports what it ACTUALLY joined — show that truth.
              setBanner({
                kind: "ok",
                node: c.result ? `✓ Connected — ${c.result}` : `✓ Connected to ${connecting.ssid}.`,
              });
              void statusQ.refetch();
              void latestCmdQ.refetch();
            } else if (c.state === "failed") {
              window.clearInterval(t);
              setConnecting(null);
              setBanner({ kind: "err", node: `✗ Join failed: ${c.result ?? "unknown error"}` });
            }
          }
        } catch {
          /* keep waiting */
        }
        if (tries >= 30) {
          window.clearInterval(t);
          setConnecting(null);
          setBanner({
            kind: "err",
            node: "No result after ~90 s. The box may be offline — it will still run the command the next time it comes online.",
          });
        }
      })();
    }, 3_000);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connecting?.id]);

  // ── Hotel clone (harvest) ───────────────────────────────────────────────
  const [hotelPw, setHotelPw] = useState("");
  const [harvest, setHarvest] = useState<{ mode?: string; ssid?: string; error?: string; stations?: string[] } | null>(null);

  const pollHarvest = useCallback(async (): Promise<void> => {
    try {
      const s = await Api.wifi.harvest.status(deviceId);
      setHarvest(s && s.mode && s.mode !== "idle" ? s : null);
    } catch {
      /* box may be mid-handoff — ignore */
    }
  }, [deviceId]);

  // On mount: pick up a clone run already in progress; keep polling only
  // while one is active.
  useEffect(() => {
    void pollHarvest();
  }, [pollHarvest]);
  useEffect(() => {
    if (!harvest) return;
    const t = window.setInterval(() => void pollHarvest(), 2_500);
    return () => window.clearInterval(t);
  }, [harvest !== null, pollHarvest]);

  async function harvestStart(): Promise<void> {
    if (!effectiveSsid) {
      setBanner({ kind: "err", node: "Pick or type the hotel network first." });
      return;
    }
    setBanner(null);
    try {
      const d = await Api.wifi.harvest.start(deviceId, { ssid: effectiveSsid, ap_password: hotelPw.trim() || null });
      if (!d.ok) throw new Error(d.error ?? "failed to start");
      setBanner({
        kind: "info",
        node: (
          <>
            Clone mode started. <b>On your phone:</b> make sure you already finished the hotel
            sign-in, then toggle Wi-Fi off/on (or just wait) so the phone joins &ldquo;{effectiveSsid}&rdquo;
            — it will pick this box, the closer one. If you were driving this page through the
            cloud, the phone loses internet when it hops over — open{" "}
            <b>http://192.168.4.1/wifi</b> on it to finish.
          </>
        ),
      });
      await pollHarvest();
    } catch (e) {
      setBanner({ kind: "err", node: `✗ ${(e as Error).message}` });
    }
  }

  async function harvestFinish(mac: string): Promise<void> {
    setBanner({
      kind: "info",
      node: `Cloning ${mac} and joining the real hotel… this takes up to 30 seconds. If the phone was on this page it will lose the box — check the TV, or reopen this page once the box is back online.`,
    });
    try {
      await Api.wifi.harvest.finish(deviceId, { mac, password: hotelPw.trim() || null });
    } catch {
      /* expected — the box leaves mid-call */
    }
    window.setTimeout(() => void pollHarvest(), 15_000);
  }

  async function harvestAbort(): Promise<void> {
    try {
      await Api.wifi.harvest.abort(deviceId);
    } catch {
      /* best effort */
    }
    setHarvest(null);
    setBanner({ kind: "info", node: "Clone mode cancelled." });
  }

  // ── Bluetooth lifeline ──────────────────────────────────────────────────
  const btQ = useQuery({
    queryKey: ["wifi-bt-status", deviceId],
    queryFn: () => Api.wifi.bt.status(deviceId),
    retry: false,
  });
  const phones = btQ.data?.phones ?? [];

  const [btScanning, setBtScanning] = useState(false);
  const [btDevices, setBtDevices] = useState<{ mac: string; name: string | null }[]>([]);

  async function btScan(): Promise<void> {
    setBanner({
      kind: "info",
      node: "Scanning 2 min — on your Android, open Settings → Bluetooth so the phone is discoverable, then watch this list.",
    });
    try {
      await Api.wifi.bt.scanStart(deviceId);
      setBtScanning(true);
    } catch (e) {
      setBanner({ kind: "err", node: `✗ ${(e as Error).message}` });
    }
  }

  useEffect(() => {
    if (!btScanning) return;
    let elapsed = 0;
    const t = window.setInterval(() => {
      elapsed += 3;
      void Api.wifi.bt.devices(deviceId)
        .then((d) => setBtDevices(d.devices ?? []))
        .catch(() => undefined);
      if (elapsed >= 120) setBtScanning(false);
    }, 3_000);
    return () => window.clearInterval(t);
  }, [btScanning]);

  async function btPair(mac: string, name: string): Promise<void> {
    setBanner({ kind: "info", node: `Pairing with ${mac} — accept on the phone if it asks.` });
    try {
      const d = await Api.wifi.bt.pair(deviceId, { mac, name });
      if (!d.ok) throw new Error(d.error ?? "pair failed");
      setBanner({
        kind: "ok",
        node: "✓ Paired. Now enable Bluetooth tethering on the phone (Settings → Hotspot & tethering → Bluetooth tethering) and the box can ride it whenever Wi-Fi is missing.",
      });
      setBtScanning(false);
      void btQ.refetch();
    } catch (e) {
      setBanner({ kind: "err", node: `✗ ${(e as Error).message}` });
    }
  }

  async function btForget(mac: string): Promise<void> {
    if (!window.confirm("Remove this phone from the lifeline list?")) return;
    try {
      await Api.wifi.bt.forget(deviceId, { mac });
    } catch {
      /* best effort */
    }
    void btQ.refetch();
  }

  async function btConnect(): Promise<void> {
    setBanner({
      kind: "info",
      node: "Trying the lifeline (up to 30 s)… make sure Bluetooth tethering is ON on the phone.",
    });
    try {
      const d = await Api.wifi.bt.connect(deviceId);
      setBanner(
        d.ok
          ? {
              kind: "ok",
              node: `✓ Lifeline is up via ${d.phone ?? "a phone"}. Tap "Disconnect lifeline" when you are done testing (it takes priority over other networks while up).`,
            }
          : {
              kind: "err",
              node: "✗ Lifeline did not come up. Is Bluetooth tethering enabled on the phone? (Settings → Hotspot & tethering → Bluetooth tethering)",
            },
      );
    } catch (e) {
      setBanner({ kind: "err", node: `✗ ${(e as Error).message}` });
    }
    void btQ.refetch();
  }

  async function btDisconnect(): Promise<void> {
    try {
      await Api.wifi.bt.disconnect(deviceId);
    } catch {
      /* best effort */
    }
    setBanner({ kind: "info", node: "Lifeline disconnected." });
    void btQ.refetch();
  }

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div className="max-w-xl">
      <h1 className="font-display text-3xl text-ivory flex items-center gap-3">
        <WifiIcon size={24} className="text-gold" /> Wi-Fi
        {selectedLabel && (
          <span className="text-base text-parchment/70">· {selectedLabel}</span>
        )}
      </h1>
      <div className="ornament-rule" />

      {pis.length > 0 && (
        <div className="mb-6">
          <label className="block text-xs text-parchment mb-1" htmlFor="wifi-pi">
            Playback device
          </label>
          <select
            id="wifi-pi"
            className={INPUT_CLS}
            value={deviceId ?? ""}
            onChange={(e) => setPicked(e.target.value || undefined)}
          >
            {pis.map((p) => (
              <option key={p.device_id} value={p.device_id}>
                {p.label} — {p.online ? "online" : "offline"}
              </option>
            ))}
          </select>
        </div>
      )}

      {unreachable && (
        <div className="rounded-md border border-gold/40 bg-gold/10 px-3 py-2.5 text-sm text-goldHi leading-relaxed mb-6">
          Live link to the box is down — the readings below are stale or unknown. Queued joins
          still work: type the network name, tap Connect, and the box runs it within ~15 s of
          having ANY connection (even the phone lifeline).
        </div>
      )}

      <div className="space-y-8">
        {/* Connected now */}
        <section className="space-y-2">
          <div className="section-label">Connected now</div>
          <Row
            label="Wi-Fi network"
            value={unreachable ? "unknown — box unreachable" : (status?.ssid ?? "not connected")}
          />
          <Row
            label="Signal"
            value={
              unreachable || !status?.ssid
                ? "—"
                : [
                    status.signal_dbm != null ? `${status.signal_dbm} dBm` : null,
                    status.band,
                    status.tx_mbps != null ? `${status.tx_mbps} Mbps` : null,
                  ]
                    .filter(Boolean)
                    .join(" · ") || "—"
            }
          />
          <Row
            label="Wi-Fi IP"
            value={!unreachable && status?.ssid && status.wifi_ip ? status.wifi_ip : "—"}
          />
          <Row
            label="Ethernet"
            value={
              unreachable
                ? "—"
                : status?.ethernet_connected
                  ? `connected${status.ethernet_ip ? ` · ${status.ethernet_ip}` : ""}`
                  : "not connected"
            }
          />
          {lastCmd && (
            <>
              <Row
                label="Last command"
                value={`connect ${lastCmd.ssid} — ${lastCmd.state} · ${ago(lastCmd.updated_at)}`}
              />
              {lastCmd.result && (
                <div
                  className={clsx(
                    "rounded-md border px-3 py-2 text-sm font-mono leading-relaxed",
                    lastCmd.state === "failed"
                      ? "bg-vermilion/15 border-vermilion/50 text-vermilionHi"
                      : "bg-jade/10 border-jade/40 text-emerald-300",
                  )}
                >
                  {lastCmd.state === "failed" ? "✗ " : "✓ "}
                  {lastCmd.result}
                </div>
              )}
            </>
          )}
          {!unreachable && status?.ap_mode && (
            <div className="rounded-md border border-gold/40 bg-gold/10 px-3 py-2 text-sm text-goldHi">
              The box is broadcasting its setup hotspot — join &ldquo;WallaceTV&rdquo; and open
              http://192.168.4.1/wifi if this page stops responding.
            </div>
          )}
        </section>

        {/* Change network — stays usable with the tunnel down: the join queues
            on the cloud and the box picks it up over any connection. */}
        <section className="space-y-2">
          <div className="section-label">Change network</div>
          <div className="card p-4 space-y-3">
            {!unreachable && (
              <div>
                <label className="block text-xs text-parchment mb-1" htmlFor="wifi-ssid">
                  Available networks
                </label>
                <select
                  id="wifi-ssid"
                  className={INPUT_CLS}
                  value={ssid}
                  onChange={(e) => setSsid(e.target.value)}
                  disabled={scanQ.isLoading}
                >
                  <option value="">
                    {scanQ.isLoading
                      ? "— scanning —"
                      : networks.length === 0
                        ? "No networks found — type the name below"
                        : "— pick a network —"}
                  </option>
                  {networks.map((n) => (
                    <option key={n.ssid} value={n.ssid}>
                      {n.ssid} — {n.signal}% {n.security ? "🔒" : "🔓"}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-xs text-parchment mb-1" htmlFor="wifi-manual-ssid">
                {unreachable ? "Network name" : "…or type the network name"}{" "}
                <span className="text-parchment/50">(hidden networks, or when the scan shows nothing)</span>
              </label>
              <input
                id="wifi-manual-ssid"
                type="text"
                autoComplete="off"
                placeholder="network name (SSID)"
                className={INPUT_CLS}
                value={manualSsid}
                onChange={(e) => setManualSsid(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs text-parchment mb-1" htmlFor="wifi-pw">
                Password <span className="text-parchment/50">(leave blank for open networks)</span>
              </label>
              <input
                id="wifi-pw"
                type="text"
                autoComplete="off"
                placeholder="wifi password"
                className={INPUT_CLS}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button className="pill" onClick={() => void doConnect()} disabled={!effectiveSsid || !!connecting}>
                {connecting ? <Loader2 size={15} className="animate-spin" /> : <WifiIcon size={15} />}
                {connecting ? "Connecting…" : "Connect"}
              </button>
              {!unreachable && (
                <button
                  className="pill-ghost"
                  onClick={() => void scanQ.refetch()}
                  disabled={scanQ.isFetching}
                >
                  <RefreshCw size={14} className={scanQ.isFetching ? "animate-spin" : undefined} />
                  Rescan networks
                </button>
              )}
            </div>
          </div>
        </section>

        {/* Hotel clone + Bluetooth lifeline drive the box's radio directly
            through the tunnel — they need the live link. */}
        {unreachable ? (
          <div className="card p-4 text-sm text-parchment">
            Hotel clone and the Bluetooth lifeline need the live link to the box — they&apos;ll
            reappear when it&apos;s reachable again.
          </div>
        ) : (
          <>
          {/* Hotel clone */}
          <section className="space-y-2">
            <div className="section-label">Hotel Wi-Fi — clone my phone</div>
            <div className="card p-4 space-y-3">
              <p className="text-xs text-parchment leading-relaxed">
                For hotels with a sign-in page: <b>①</b> on your phone, join the hotel Wi-Fi and
                finish its sign-in page. <b>②</b> pick that network above and tap &ldquo;Start clone
                mode&rdquo; — the box briefly becomes a fake &ldquo;hotel&rdquo;, and your phone hops
                onto it (same name, closer). <b>③</b> the box borrows the identity your phone just
                used and joins the real hotel as it. Your phone can drop back to mobile data
                afterwards.
              </p>
              <div>
                <label className="block text-xs text-parchment mb-1" htmlFor="hotel-pw">
                  Hotel Wi-Fi password{" "}
                  <span className="text-parchment/50">(only if the hotel network itself has one)</span>
                </label>
                <input
                  id="hotel-pw"
                  type="text"
                  autoComplete="off"
                  className={INPUT_CLS}
                  value={hotelPw}
                  onChange={(e) => setHotelPw(e.target.value)}
                />
              </div>
              <button className="pill" onClick={() => void harvestStart()} disabled={!effectiveSsid}>
                Start clone mode
              </button>

              {harvest && (
                <div className="space-y-2 border-t border-hairline pt-3">
                  <Row label="Clone" value={harvest.mode ?? "—"} />
                  {harvest.error && (
                    <div className="rounded-md border border-vermilion/50 bg-vermilion/15 px-3 py-2 text-sm text-vermilionHi">
                      {harvest.error}
                    </div>
                  )}
                  {harvest.mode === "waiting" && (harvest.stations?.length ?? 0) > 0 ? (
                    <>
                      <p className="text-xs text-parchment">
                        A device joined &ldquo;{harvest.ssid}&rdquo;. If this is your phone:
                      </p>
                      {harvest.stations!.map((m) => (
                        <button
                          key={m}
                          className="block w-full text-left font-mono text-xs bg-ink/60 border border-hairline rounded px-3 py-2 text-goldHi hover:border-gold transition"
                          onClick={() => void harvestFinish(m)}
                        >
                          Clone {m} &amp; connect
                        </button>
                      ))}
                    </>
                  ) : (
                    harvest.mode === "waiting" && (
                      <p className="text-xs text-parchment">
                        Waiting for your phone to join &ldquo;{harvest.ssid}&rdquo;… (toggle Wi-Fi on
                        the phone)
                      </p>
                    )
                  )}
                  <button className="pill-ghost text-sm" onClick={() => void harvestAbort()}>
                    Cancel clone mode
                  </button>
                </div>
              )}
            </div>
          </section>

          {/* Bluetooth lifeline */}
          <section className="space-y-2">
            <div className="section-label flex items-center gap-2">
              <Bluetooth size={13} /> Phone lifeline (Bluetooth)
            </div>
            <div className="card p-4 space-y-3">
              <p className="text-xs text-parchment leading-relaxed">
                Pair your Android once at home. Afterwards, whenever the box can&rsquo;t get online
                it can ride the phone&rsquo;s Bluetooth tethering — so you can reach it and fix
                Wi-Fi without the hotspot dance. Keep &ldquo;Bluetooth tethering&rdquo; enabled on
                the phone when travelling.
              </p>

              {phones.length === 0 ? (
                <Row label="Lifelines" value="none paired" />
              ) : (
                phones.map((p) => (
                  <div
                    key={p.mac}
                    className="flex items-center justify-between gap-4 bg-ink/60 border border-hairline rounded-md px-3 py-2"
                  >
                    <span className="section-label text-[10px] flex items-center gap-1.5">
                      <Smartphone size={12} /> {p.name}
                    </span>
                    <span className="font-mono text-sm text-goldHi flex items-center gap-2">
                      {p.profile_active
                        ? `ACTIVE${p.bt_ip ? ` · ${p.bt_ip}` : ""}`
                        : p.connected
                          ? "connected"
                          : p.paired
                            ? "paired"
                            : "not paired"}
                      <button
                        className="text-vermilionHi/70 hover:text-vermilionHi text-xs"
                        onClick={() => void btForget(p.mac)}
                      >
                        forget
                      </button>
                    </span>
                  </div>
                ))
              )}

              <div className="flex flex-wrap items-center gap-2">
                <button className="pill-ghost" onClick={() => void btScan()} disabled={btScanning}>
                  {btScanning ? <Loader2 size={14} className="animate-spin" /> : <Bluetooth size={14} />}
                  {btScanning ? "Scanning…" : "Add a phone"}
                </button>
                <button className="pill-ghost" onClick={() => void btConnect()}>
                  Connect lifeline now
                </button>
                <button className="pill-ghost" onClick={() => void btDisconnect()}>
                  Disconnect lifeline
                </button>
              </div>

              {btScanning && btDevices.length > 0 && (
                <div className="space-y-1.5">
                  {btDevices.map((d) => {
                    const have = phones.some((p) => p.mac === d.mac);
                    return have ? (
                      <div
                        key={d.mac}
                        className="flex items-center gap-2 font-mono text-xs px-3 py-2 text-parchment/50"
                      >
                        <Check size={12} /> {d.name ?? "?"} · {d.mac} — already added
                      </div>
                    ) : (
                      <button
                        key={d.mac}
                        className="block w-full text-left font-mono text-xs bg-ink/60 border border-hairline rounded px-3 py-2 text-goldHi hover:border-gold transition"
                        onClick={() => void btPair(d.mac, d.name ?? "phone")}
                      >
                        {d.name ?? "?"} · {d.mac}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
          </>
        )}

        <BannerBox banner={banner} />
      </div>
    </div>
  );
}
